import {
  CODING_SUBJECTS,
  THEORY_SUBJECTS,
  type SubjectData,
  type SubjectPost,
} from "@/data/subject-posts";
import { getPostImage } from "@/lib/postImageUtils";
import { SUBJECT_LABELS } from "@/lib/route-layout";

/**
 * 한길 지도(TrailMap)의 "길 순서" 단일 소스 (JCG-2061).
 *
 * 지역 = MDX 글 페이지 1개. 카테고리(`/coding/c-language` 등) 안의 글들이
 * scope(기초 → 연산자 → …) 등장 순서로 한 줄로 이어진다.
 *
 * 데이터 원본은 과목 글 목록 SSOT 인 `src/data/subject-posts.ts`
 * (`recommendedPosts` + `posts`). 클라이언트 컴포넌트(TrailMap)에서 직접 import 하므로
 * 이 파일은 서버 전용 의존성을 두지 않는다.
 */
export interface TrailStationDef {
  /** 1부터 시작하는 지역 순번 */
  no: number;
  /** 표지판 라벨(말미 괄호 보충 설명 제거한 간결 제목) */
  title: string;
  /** 이동 경로 (`/coding/c-language/pointer`) */
  href: string;
  /** 구간(기초·연산자 등) — 길 위 지역 라벨용 */
  scope: string;
  /** 현재 지역 노드에 표시할 마크(우선도 이모지) */
  mark: string;
  /** 폴더 그리드와 동일한 글 아이콘 경로(개별 글 이미지, 실패 시 scope 폴백은 렌더 단계에서) */
  iconUrl: string;
}

export interface TrailCategoryOption {
  href: string;
  name: string;
}

export const TRAIL_QUERY_PARAM = "trail";

const TRAIL_SUBJECTS: SubjectData[] = [...CODING_SUBJECTS, ...THEORY_SUBJECTS];

const SUBJECT_BY_HREF: Record<string, SubjectData> = (() => {
  const map: Record<string, SubjectData> = {};
  for (const subject of TRAIL_SUBJECTS) {
    map[subject.href] = subject;
  }
  return map;
})();

function stripTrailing(path: string): string {
  return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
}

function toPathname(path: string): string {
  return stripTrailing(path.split(/[?#]/, 1)[0] || "/");
}

function normalizeCategoryPath(categoryPath: string | null | undefined): string | null {
  if (!categoryPath) return null;
  const normalized = toPathname(categoryPath);
  return SUBJECT_BY_HREF[normalized] ? normalized : null;
}

export function isPathInTrail(pathname: string, categoryPath: string): boolean {
  const normalizedCategory = normalizeCategoryPath(categoryPath);
  if (!normalizedCategory) return false;

  const currentPath = toPathname(pathname);
  if (currentPath === normalizedCategory) return true;

  return buildTrailStations(normalizedCategory).some(
    (station) => stripTrailing(station.href) === currentPath,
  );
}

/**
 * 글/카테고리 pathname → 그 글이 속한(또는 그 자신인) 카테고리(인덱스) 경로.
 *
 * 공유 글(`/coding/common/*`)처럼 여러 과목 지도에 포함되는 글은 `requestedCategoryPath`가
 * 유효하고 현재 글을 실제로 포함할 때 그 지도 컨텍스트를 우선한다(JCG-2487).
 *
 * - 글 라우트 `/root/cat/slug` 3세그먼트(`/coding/c-language/pointer`): cat 이 알려진
 *   과목 카테고리일 때 카테고리 경로 반환.
 * - 카테고리 인덱스 `/root/cat` 2세그먼트(`/coding/c-language`): 그 경로 자신이 알려진
 *   과목 카테고리일 때 그대로 반환(JCG-2098) — 인덱스 페이지에서도 지도를 그리되 "현재
 *   정거장"은 없다(어떤 글 href 와도 일치하지 않음).
 *
 * 그 외엔 null(지도 미노출).
 */
export function getTrailCategoryPath(
  pathname: string,
  requestedCategoryPath?: string | null,
): string | null {
  const requested = normalizeCategoryPath(requestedCategoryPath);
  if (requested && isPathInTrail(pathname, requested)) return requested;

  const seg = toPathname(pathname).split("/").filter(Boolean);
  if (seg.length < 2) return null;
  const categoryPath = `/${seg[0]}/${seg[1]}`;
  const category = SUBJECT_BY_HREF[categoryPath] ? categoryPath : null;
  if (!category) return null;
  if (isPathInTrail(pathname, category)) return category;

  return getTrailCategoryOptions(pathname)[0]?.href ?? category;
}

/**
 * 지도 Section·시트 라벨. 지도가 실제로 내용을 갖는 카테고리(getTrailCategoryPath)에서만
 * "C언어 지도"처럼 접두어를 붙이고, 그 외(길 없음)는 "지도".
 *
 * 데스크탑 셸(ArticleShell)·글 모바일 지도 도구·과목 인덱스 모바일 도구가 같은 라벨을 쓰는
 * 단일 출처(JCG-3454) — 문구를 바꿀 때 세 곳이 어긋나지 않게 여기만 고친다.
 */
export function getTrailMapLabel(
  pathname: string,
  requestedCategoryPath?: string | null,
): string {
  const category = getTrailCategoryPath(pathname, requestedCategoryPath);
  return category ? `${SUBJECT_LABELS[category] ?? ""} 지도`.trim() : "지도";
}

export function getTrailHref(href: string, categoryPath?: string | null): string {
  const category = normalizeCategoryPath(categoryPath);
  if (!category || !isPathInTrail(href, category)) return href;
  if (getTrailCategoryPath(href) === category) return href;

  const url = new URL(href, "https://jcg.local");
  url.searchParams.set(TRAIL_QUERY_PARAM, category);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function getTrailCategoryOptions(pathname: string): TrailCategoryOption[] {
  return TRAIL_SUBJECTS
    .filter((subject) => isPathInTrail(pathname, subject.href))
    .map((subject) => ({
      href: subject.href,
      name: subject.name,
    }));
}

/** 표지판이 길어지지 않도록 제목 말미의 보충 괄호를 제거. */
function shortTitle(title: string): string {
  return title.replace(/\s*\([^()]*\)\s*$/, "").trim() || title;
}

/**
 * 카테고리의 지역 목록을 길 순서대로 생성.
 *
 * 순서 규칙: `recommendedPosts` + `posts` 를 합쳐 scope 첫 등장 순으로 그룹화하고,
 * 그룹 안에서는 등장 순서를 유지한다(권장 글이 먼저). href 중복은 첫 항목만 남긴다.
 */
export function buildTrailStations(categoryPath: string): TrailStationDef[] {
  const subject = SUBJECT_BY_HREF[categoryPath];
  if (!subject) return [];

  const all: SubjectPost[] = [...subject.recommendedPosts, ...subject.posts];

  const seen = new Set<string>();
  const byScope = new Map<string, SubjectPost[]>();

  for (const post of all) {
    if (seen.has(post.href)) continue;
    seen.add(post.href);
    let group = byScope.get(post.scope);
    if (!group) {
      group = [];
      byScope.set(post.scope, group);
    }
    group.push(post);
  }

  const ordered: SubjectPost[] = [...byScope.values()].flat();

  return ordered.map((post, i) => ({
    no: i + 1,
    title: shortTitle(post.title),
    href: post.href,
    scope: post.scope,
    mark: post.priority || "📖",
    iconUrl: getPostImage(post.href),
  }));
}

/**
 * 여러 과목 학습 경로에 포함된 페이지 href 합집합.
 *
 * 공통 코딩 글처럼 여러 지도에 반복되는 페이지도 첫 등장 한 번만 남긴다. 공부 진행상황의
 * 전체 분모가 상세 출석/지도와 같은 페이지 집합을 쓰도록 서버 집계에서도 공유한다(JCG-3101).
 */
export function getUniqueTrailPageHrefs(
  categoryPaths: readonly string[],
): string[] {
  const seen = new Set<string>();

  for (const categoryPath of categoryPaths) {
    for (const station of buildTrailStations(categoryPath)) {
      seen.add(station.href);
    }
  }

  return Array.from(seen);
}
